<template>
  <section class="latest-alarms">
    <header class="latest-alarms__header">
      <h2 class="latest-alarms__title">{{ $t('attention-map-panel.latest-title') }}</h2>
      <button
        type="button"
        class="latest-alarms__see-all"
        @click="$emit('see-all')"
      >
        {{ $t('attention-map-panel.see-all') }}{{ hiddenLabel }} ›
      </button>
    </header>

    <p v-if="!alarms.length" class="latest-alarms__empty">
      {{ $t('attention-map-panel.latest-empty') }}
    </p>

    <ul v-else class="latest-alarms__list">
      <li
        v-for="alarm in alarms"
        :key="alarm.key"
        class="latest-alarms__item"
        :class="`latest-alarms__item--${alarm.classification}`"
      >
        <button
          type="button"
          class="latest-alarms__button"
          @click="$emit('select', alarm)"
        >
          <!--
            Onde o alarme está e há quanto tempo. Sobe para o topo porque é o que
            situa quem lê antes de ele chegar no nome — e porque o tempo ao lado
            do nome roubava a largura do que mais importa.

            Só Ativo e Instalação: Empresa e Regional saíram porque Ativo mais
            Instalação já desambigua. Existem três Ativos `04T1` na frota, em
            instalações diferentes, e a instalação basta para separá-los.
          -->
          <span class="latest-alarms__meta">
            <span class="latest-alarms__where">{{ whereOf(alarm) }}</span>
            <span class="latest-alarms__age">{{ ageOf(alarm) }}</span>
          </span>

          <!--
            O assunto do card. É o único elemento que nunca trunca: quebra em
            quantas linhas precisar, porque nome de alarme cortado no meio não diz
            o que está acontecendo.
          -->
          <span class="latest-alarms__name">{{ alarm.name }}</span>

          <!--
            Rodapé: gravidade escrita, status e origem.

            A pílula substituiu o selo `!!!`. Ela cumpre o mesmo papel — ler a
            gravidade **sem depender da cor**, que numa TV de parede e para quem
            não distingue vermelho de âmbar não é garantia — e cumpre melhor,
            porque palavra não precisa ser decifrada.

            O preenchimento da pílula diz **gravidade**, e não atuação: Crítico e
            Urgente vêm sólidos, Ordinário vem discreto. Numa frota real dois
            terços dos alarmes são Ordinários, e pílula sólida em todo card
            deixaria de ser destaque para virar textura.
          -->
          <span class="latest-alarms__footer">
            <span
              class="latest-alarms__pill"
              :class="`latest-alarms__pill--${alarm.classification}`"
            >{{ $t(`attention-map-panel.classification.${alarm.classification}`) }}</span>

            <!--
              A gramática de status da tela de Alarmes e Avisos: a cor do sino
              diz se o alarme está atuando, e o selo de visto diz se alguém já
              reconheceu. Dois eixos, dois sinais — e o sino apagado é o que diz
              que o alarme está pendente de reconhecimento, sem gastar uma linha
              inteira do card para repeti-lo.
            -->
            <span
              class="latest-alarms__status"
              :class="{ 'latest-alarms__status--acted': alarm.acted }"
              :title="statusLabelOf(alarm)"
            >
              <s-icon name="alarm" />
              <s-icon
                v-if="alarm.recognized"
                class="latest-alarms__status-check"
                name="check"
              />
            </span>

            <span class="latest-alarms__category">
              {{ $t(`attention-map-panel.origin.${alarm.category}`) }}
            </span>
          </span>
        </button>
      </li>
    </ul>
  </section>
</template>

<script>
import describeElapsed from '@/services/dateTime/elapsedTime';

export default {
  name: 'LatestAlarms',
  props: {
    alarms: {
      type: Array,
      default: () => [],
    },
    /** Quantos alarmes existem no recorte, contando os que não caberam. */
    total: {
      type: Number,
      default: 0,
    },
    /** Relógio da página, para o tempo decorrido não congelar entre ciclos. */
    now: {
      type: Number,
      default: () => Date.now(),
    },
  },
  computed: {
    /**
     * O total entre parênteses no "Ver todos", quando a lista está cortada.
     *
     * Sem ele a lista mente por omissão: trinta linhas parecem ser tudo o que
     * existe, e numa frota real são trinta de milhares.
     */
    hiddenLabel() {
      return this.total > this.alarms.length ? ` (${this.total})` : '';
    },
  },
  methods: {
    /**
     * O status por extenso, no `title` do sino.
     *
     * O desenho responde a distância; a palavra responde a quem para em cima e
     * quer certeza. Os três estados são os mesmos de Alarmes e Avisos, com os
     * mesmos nomes — quem aprendeu lá não reaprende aqui.
     */
    statusLabelOf(alarm) {
      if (!alarm.acted) {
        return this.$t('attention-map-panel.status.not-acted-not-recognized');
      }

      return alarm.recognized
        ? this.$t('attention-map-panel.status.acted-recognized')
        : this.$t('attention-map-panel.status.acted-not-recognized');
    },
    /**
     * Onde o alarme está: Ativo e Instalação, numa linha só.
     *
     * É contexto, e contexto pode truncar.
     */
    whereOf(alarm) {
      return [alarm.assetName, alarm.installation].filter(Boolean).join(' · ');
    },
    /**
     * Há quanto tempo o alarme está **aberto**, em unidades de calendário e não em
     * horas acumuladas.
     *
     * `9840h` é um número que ninguém lê; `1a 1m 12d` responde na hora se o alarme
     * abriu hoje ou há um ano. Conta desde a abertura, e não desde a última
     * atuação: um Pendente que volta a atuar continua contando desde que abriu.
     * Ver `elapsedTime.js`.
     */
    ageOf(alarm) {
      const parts = describeElapsed(alarm.openedAt, this.now);

      if (!parts) {
        return this.$t('attention-map-panel.age-unknown');
      }

      // Só o tempo, sem "há" na frente: a coluna é estreita e a palavra não
      // acrescenta — o número ao lado de um alarme já se lê como idade dele.
      return parts
        .map(({ unit, value }) => this.$t(
          `attention-map-panel.age.${unit}`,
          { count: value },
        ))
        .join(' ');
    },
  },
};
</script>

<style lang="sass" scoped>
@import "src/styles/components/communication-status-panel-fluid"

// Os `cqw` daqui medem a própria lista, que tem largura definida pela página
// (`clamp(22rem, 30%, 44rem)`). Numa TV de parede a coluna é mais larga, e o
// texto cresce com ela em vez de virar uma tira de letra miúda no canto.
.latest-alarms
  background: var(--panel-background)
  container-type: inline-size
  border-left: 1px solid var(--panel-border)
  display: flex
  flex-direction: column
  height: 100%
  min-height: 0

  &__header
    align-items: baseline
    display: flex
    justify-content: space-between
    padding: 0.7rem 1rem 0.5rem

  // O título é lido uma vez, quando alguém chega na tela; os cards são lidos o
  // tempo todo. Em 4,3cqw ele dominava a coluna e empurrava a lista para baixo,
  // custando um card inteiro de altura numa TV.
  &__title
    color: var(--panel-text)
    +panel-fluid(font-size, 0.88rem, 3.6cqw, 1.9rem)
    font-weight: 700
    letter-spacing: 0.04em
    margin: 0
    text-transform: uppercase

  &__see-all
    background: transparent
    border: none
    color: var(--panel-match)
    cursor: pointer
    +panel-fluid(font-size, 0.7rem, 2.8cqw, 1.5rem)
    padding: 0

    &:hover
      text-decoration: underline

  &__empty
    color: var(--panel-text-muted)
    +panel-fluid(font-size, 0.8rem, 3.2cqw, 1.7rem)
    padding: 0 1rem

  &__list
    list-style: none
    margin: 0
    // A lista rola sozinha: o mapa ao lado mantém a altura da tela.
    overflow-y: auto
    padding: 0 0.8rem 0.8rem

  // A faixa da esquerda é a leitura de longe: numa lista de trinta, é ela que
  // diz onde estão os críticos sem ninguém ler uma palavra.
  &__item
    background: var(--panel-surface)
    border: 1px solid var(--panel-border)
    border-left: 5px solid var(--panel-border)
    border-radius: 6px
    margin-bottom: 0.5rem

    &--critical
      border-left-color: var(--classification-critical)

    &--urgent
      border-left-color: var(--classification-urgent)

    &--ordinary
      border-left-color: var(--classification-ordinary)

  // A escada de corpos dentro do card é curta de propósito: 2,6cqw na pílula a
  // 3,3cqw no nome, razão de 1,27. Antes ia de 2,5 a 3,8 — razão de 1,52 —, e com
  // quatro corpos diferentes em três linhas a lista virava degrau atrás de
  // degrau, cansando quem percorre trinta cards.
  //
  // O respiro cresceu junto. Hierarquia feita só de tamanho precisa de contraste
  // grande; feita de tamanho **e** espaço, o contraste pode ser menor e o card
  // continua legível de longe.
  &__button
    background: transparent
    border: none
    color: var(--panel-text)
    cursor: pointer
    display: flex
    flex-direction: column
    +panel-fluid(gap, 0.3rem, 1.3cqw, 0.7rem)
    +panel-fluid(padding-block, 0.55rem, 2.3cqw, 1.2rem)
    +panel-fluid(padding-inline, 0.7rem, 3.1cqw, 1.5rem)
    text-align: left
    width: 100%

    &:hover
      background: var(--panel-chip-hover)

  &__meta
    align-items: baseline
    display: flex
    gap: 0.6rem
    width: 100%

  // Contexto pode cortar; nome não.
  &__where
    color: var(--panel-text-muted)
    +panel-fluid(font-size, 0.72rem, 2.8cqw, 1.7rem)
    min-width: 0
    overflow: hidden
    text-overflow: ellipsis
    white-space: nowrap

  &__age
    color: var(--panel-text-muted)
    +panel-fluid(font-size, 0.72rem, 2.8cqw, 1.7rem)
    flex: none
    font-variant-numeric: tabular-nums
    margin-left: auto
    white-space: nowrap

  // O assunto do card. Quebra em quantas linhas precisar: nome de alarme cortado
  // no meio não diz o que está acontecendo.
  &__name
    +panel-fluid(font-size, 0.84rem, 3.3cqw, 2rem)
    font-weight: 600
    line-height: 1.4
    overflow-wrap: anywhere

  &__footer
    align-items: center
    display: flex
    gap: 0.55rem
    min-width: 0
    width: 100%

  // A gravidade escrita. Crítico e Urgente sólidos; Ordinário discreto, porque
  // é a maioria dos alarmes de uma frota real e destaque que vale para todos não
  // destaca ninguém.
  &__pill
    border-radius: 999px
    flex: none
    font-weight: 700
    +panel-fluid(font-size, 0.66rem, 2.6cqw, 1.5rem)
    letter-spacing: 0.05em
    line-height: 1.9
    padding: 0 0.7rem
    text-transform: uppercase

    &--critical
      background: var(--classification-critical)
      color: var(--classification-ink-critical)

    &--urgent
      background: var(--classification-urgent)
      color: var(--classification-ink-urgent)

    &--ordinary
      background: transparent
      box-shadow: inset 0 0 0 1px var(--classification-ordinary)
      color: var(--classification-ordinary)

  // O sino apagado é o alarme que já normalizou e ninguém reconheceu — o
  // Pendente. Aceso, o que está atuando agora. Não veste cor de gravidade: essa
  // é a da pílula ao lado, e dois sinais com a mesma cor viram um sinal só.
  &__status
    color: var(--panel-text-muted)
    flex: none
    +panel-fluid(font-size, 0.72rem, 2.8cqw, 1.7rem)
    line-height: 1
    margin-left: auto
    position: relative

    &--acted
      color: var(--panel-text)

  // O visto fica sobre o sino, como em Alarmes e Avisos, e em verde: ele não
  // diz gravidade nenhuma, diz que alguém já olhou.
  &__status-check
    color: var(--condition-normal)
    font-size: 0.7em
    position: absolute
    right: -0.35em
    top: -0.2em

  &__category
    color: var(--panel-text-muted)
    +panel-fluid(font-size, 0.66rem, 2.7cqw, 1.6rem)
    flex: none
    letter-spacing: 0.04em
    text-transform: uppercase
    white-space: nowrap
</style>
